/**
 * WSA-Enlil, fast (MB, 27 Oct 2026: the animation was "terribly slow to load"; docs/specs/space-weather.md §5.2).
 * Two parts, shared by Teslyn Field, Teslyn Cloud and the Edge Function `space-weather-poller`:
 *
 * - **Teslyn Cloud's copy.** NOAA SWPC publishes a run as about 169 JPEG frames listed in `enlil.json`. The poller
 *   copies each new run once into Teslyn Cloud's own storage (bucket `space-weather`, `enlil/<run>/<file>.jpg`, the
 *   frames unchanged), a part each run, and writes `enlil/manifest.json` only once every frame of the run is held, so
 *   the pages never see half a run. `enlilCopyPlan` decides what one run does.
 * - **Progressive playing.** The pages load frames in order, several at once (`loadInOrder`), start playing once the
 *   first `ENLIL_START_AFTER` are in and keep loading the rest behind the animation; a phone loads every second frame
 *   (`enlilPlayOrder`). Frames come from the copy when its manifest names a complete run, else from SWPC
 *   (`enlilFramesToLoad`). Credits to NOAA SWPC stay on the pages.
 *
 * Zero imports: copied as it is to supabase/functions/_shared/ by tools/dev/copy-edge-shared.mjs.
 */

export const ENLIL_BUCKET = 'space-weather';
export const ENLIL_MANIFEST = 'enlil/manifest.json';
export const ENLIL_SWPC = 'https://services.swpc.noaa.gov';
/** Frames copied per poller run at most (a new run is complete in three runs, 45 minutes). */
export const ENLIL_COPY_PER_RUN = 60;
/** Frames loaded at once by a page (a browser's connections to one host). */
export const ENLIL_LOAD_AT_ONCE = 6;
/** The animation starts once this many frames are in (about 1 s of it); the rest load behind it. */
export const ENLIL_START_AFTER = 8;

const SWPC_PATH = /^\/images\/animations\/enlil\/([A-Za-z0-9_.-]{1,120}\.jpg)$/;
const RUN = /^[0-9a-f]{8}-[0-9]{1,4}$/;

/** A frame SWPC lists: its path on services.swpc.noaa.gov and its time (from the file name). */
export interface EnlilListedFrame {
  readonly path: string;
  readonly time: string | null;
}

/** A frame of the copy: SWPC's path, its object in the bucket and its time. */
export interface EnlilCopyFrame {
  readonly path: string;
  readonly name: string;
  readonly time: string | null;
}

/** `enlil/manifest.json`: the run complete in the copy. */
export interface EnlilManifest {
  readonly version: 1;
  readonly run: string;
  readonly stored_at: string;
  readonly frames: readonly EnlilCopyFrame[];
}

/** Whether `path` is one of SWPC's Enlil frames (nothing else is ever fetched or stored). */
export function isEnlilPath(path: string): boolean {
  return SWPC_PATH.test(path) && !path.includes('..');
}

/** A run's key: an FNV-1a hash of its frame list and the frame count ("1a2b3c4d-169"). */
export function enlilRunKey(paths: readonly string[]): string {
  let h = 0x811c9dc5;
  const text = paths.join('\n');
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${h.toString(16).padStart(8, '0')}-${paths.length}`;
}

/** The frame's object in the bucket: `enlil/<run>/<file>`. */
export function enlilObjectName(run: string, path: string): string {
  const m = SWPC_PATH.exec(path);
  if (!m || !RUN.test(run)) throw new Error('not an Enlil frame');
  return `enlil/${run}/${m[1]}`;
}

/** The manifest's text, checked: every frame an Enlil path stored under the run's own folder. Null when unreadable. */
export function parseEnlilManifest(text: string | null): EnlilManifest | null {
  if (!text) return null;
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    return null;
  }
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (o.version !== 1 || typeof o.run !== 'string' || !RUN.test(o.run) || typeof o.stored_at !== 'string')
    return null;
  if (!Array.isArray(o.frames) || o.frames.length === 0) return null;
  const frames: EnlilCopyFrame[] = [];
  for (const f of o.frames) {
    if (!f || typeof f !== 'object') return null;
    const r = f as Record<string, unknown>;
    if (typeof r.path !== 'string' || !isEnlilPath(r.path) || typeof r.name !== 'string') return null;
    if (r.name !== enlilObjectName(o.run, r.path)) return null;
    frames.push({ path: r.path, name: r.name, time: typeof r.time === 'string' ? r.time : null });
  }
  return { version: 1, run: o.run, stored_at: o.stored_at, frames };
}

/** What one poller run does with SWPC's current list, the manifest held and the objects of the run already held. */
export interface EnlilCopyPlan {
  readonly run: string;
  /** The copy already holds this run complete: nothing to do. */
  readonly upToDate: boolean;
  /** Frames to copy now (at most `perRun`), in order. */
  readonly copy: readonly EnlilCopyFrame[];
  /** Write this manifest once the frames to copy are stored (null while the run is not complete). */
  readonly manifest: EnlilManifest | null;
}

export function enlilCopyPlan(input: {
  readonly frames: readonly EnlilListedFrame[];
  readonly manifest: EnlilManifest | null;
  /** The object names already stored under the run's folder. */
  readonly held: ReadonlySet<string>;
  readonly nowIso: string;
  readonly perRun?: number;
}): EnlilCopyPlan {
  const listed = input.frames.filter((f) => isEnlilPath(f.path));
  const run = enlilRunKey(listed.map((f) => f.path));
  if (listed.length === 0 || input.manifest?.run === run)
    return { run, upToDate: true, copy: [], manifest: null };
  const all = listed.map((f) => ({ path: f.path, name: enlilObjectName(run, f.path), time: f.time }));
  const missing = all.filter((f) => !input.held.has(f.name));
  const copy = missing.slice(0, input.perRun ?? ENLIL_COPY_PER_RUN);
  const complete = copy.length === missing.length;
  return {
    run,
    upToDate: false,
    copy,
    manifest: complete ? { version: 1, run, stored_at: input.nowIso, frames: all } : null,
  };
}

/** A frame a page loads: from the copy (`name`) or from SWPC (`name` null). */
export interface EnlilLoadFrame {
  readonly path: string;
  readonly time: string | null;
  readonly name: string | null;
}

/** The frames to load: the copy's complete run when there is one, else SWPC's own list. */
export function enlilFramesToLoad(
  swpc: readonly EnlilListedFrame[],
  manifest: EnlilManifest | null,
): readonly EnlilLoadFrame[] {
  if (manifest && manifest.frames.length > 0)
    return manifest.frames.map((f) => ({ path: f.path, time: f.time, name: f.name }));
  return swpc.filter((f) => isEnlilPath(f.path)).map((f) => ({ path: f.path, time: f.time, name: null }));
}

/** The frames a page plays, by index: every frame, or every second one on a phone (always the last one too). */
export function enlilPlayOrder(count: number, everySecond: boolean): readonly number[] {
  const out: number[] = [];
  for (let i = 0; i < count; i += everySecond ? 2 : 1) out.push(i);
  if (count > 0 && out[out.length - 1] !== count - 1) out.push(count - 1);
  return out;
}

/**
 * Loads `count` items in order, `atOnce` at a time; `onLoaded` hears each as it comes, in order of index (a later
 * one waits for the earlier ones, so the animation always has a run of frames from the first). Stops early when
 * `stopped()` says so. Resolves with how many loaded and failed.
 */
export async function loadInOrder<T>(opts: {
  readonly count: number;
  readonly load: (index: number) => Promise<T>;
  readonly onLoaded: (index: number, value: T) => void;
  readonly atOnce?: number;
  readonly stopped?: () => boolean;
}): Promise<{ readonly loaded: number; readonly failed: number }> {
  const atOnce = Math.max(1, opts.atOnce ?? ENLIL_LOAD_AT_ONCE);
  const results = new Map<number, { ok: true; value: T } | { ok: false }>();
  let next = 0;
  let delivered = 0;
  let loaded = 0;
  let failed = 0;
  const deliver = (): void => {
    while (results.has(delivered)) {
      const r = results.get(delivered)!;
      results.delete(delivered);
      if (r.ok) opts.onLoaded(delivered, r.value);
      delivered++;
    }
  };
  const worker = async (): Promise<void> => {
    while (next < opts.count && !opts.stopped?.()) {
      const i = next++;
      try {
        const value = await opts.load(i);
        results.set(i, { ok: true, value });
        loaded++;
      } catch {
        results.set(i, { ok: false });
        failed++;
      }
      if (!opts.stopped?.()) deliver();
    }
  };
  await Promise.all(Array.from({ length: Math.min(atOnce, opts.count) }, worker));
  return { loaded, failed };
}

/**
 * SWPC's frame list (`/products/animations/enlil.json`, the proxy's `noaa_enlil_frames`): only SWPC's Enlil folder,
 * each with its time from the file name (`..._20261020T060000.jpg`), oldest first. Empty when unreadable.
 */
export function parseEnlilList(body: string): readonly EnlilListedFrame[] {
  let v: unknown;
  try {
    v = JSON.parse(body);
  } catch {
    return [];
  }
  if (!Array.isArray(v)) return [];
  return v
    .map((r) => (r && typeof r === 'object' ? (r as Record<string, unknown>).url : null))
    .filter((u): u is string => typeof u === 'string' && isEnlilPath(u.trim()))
    .map((u) => u.trim())
    .map((path) => {
      const m = /_(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})\.jpg$/.exec(path);
      return {
        path,
        time: m ? new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +m[6]!)).toISOString() : null,
      };
    })
    .sort((a, b) => ((a.time ?? '') < (b.time ?? '') ? -1 : 1));
}
